import { IPolicySettingsField } from '@entities/policy';
import {
  IRoutingConfigRuleComparator,
  IRoutingConfigRuleParam,
  TRoutingConfigRuleParamValue,
} from '@entities/routing/config-version';

// The rule form's editable view of one condition / selection param (the Policies
// page's RuleParam, without the display fields — labels and option lists come from
// the GET /router/settings dictionaries at render time), converted to and from the
// GET array shape the working copy stores.

export type TDigitOperator = '=' | '<' | '>' | '<=' | '>=';

type TComparatorKey = Exclude<keyof IRoutingConfigRuleComparator, 'tags'>;

export interface IMultiParam {
  techname: string;
  type: 'multi-select';
  selected: string[];
}

export interface IDigitParam {
  techname: string;
  type: 'digit-compare';
  operator: TDigitOperator;
  value: string;
}

export interface ITagDigitParam {
  techname: string;
  type: 'tag-digit-compare';
  selected: string[];
  operator: TDigitOperator;
  value: string;
}

export type TRuleParam = IMultiParam | IDigitParam | ITagDigitParam;

// Display operator ↔ payload comparator key; the operator list and both maps derive from it.
export const OPERATOR_COMPARATORS: ReadonlyArray<{ operator: TDigitOperator; key: TComparatorKey }> =
  [
    { operator: '=', key: 'eq' },
    { operator: '>', key: 'gt' },
    { operator: '>=', key: 'gte' },
    { operator: '<', key: 'lt' },
    { operator: '<=', key: 'lte' },
  ];

export const DIGIT_OPERATORS = OPERATOR_COMPARATORS.map(item => item.operator);

const OPERATOR_TO_KEY = Object.fromEntries(
  OPERATOR_COMPARATORS.map(({ operator, key }) => [operator, key]),
) as Record<TDigitOperator, TComparatorKey>;

const KEY_TO_OPERATOR = Object.fromEntries(
  OPERATOR_COMPARATORS.map(({ operator, key }) => [key, operator]),
) as Record<string, TDigitOperator | undefined>;

// An empty param for a field picked from the dictionary.
export const newRuleParam = (field: IPolicySettingsField): TRuleParam => {
  switch (field.type) {
    case 'multi-select':
      return { techname: field.techname, type: 'multi-select', selected: [] };
    case 'tag-digit-compare':
      return { techname: field.techname, type: 'tag-digit-compare', selected: [], operator: '=', value: '' };
    default:
      return { techname: field.techname, type: 'digit-compare', operator: '=', value: '' };
  }
};

// GET array shape → form params. The dictionary decides the type of a comparator
// without tags; unknown fields fall back to the value's shape.
export const toRuleParamInputs = (
  params: IRoutingConfigRuleParam[],
  fields: IPolicySettingsField[],
): TRuleParam[] =>
  (params ?? []).map(param => {
    const techname = param.name;
    const field = fields.find(item => item.techname === techname);
    // rawValue is raw API data — a missing value reads as an empty comparator.
    const rawValue = param.rawValue ?? {};

    if (Array.isArray(rawValue)) return { techname, type: 'multi-select', selected: [...rawValue] };

    const { tags, ...comparator } = rawValue;
    const [key, number] = (Object.entries(comparator)[0] ?? []) as [string?, number?];
    const operator = (key && KEY_TO_OPERATOR[key]) || '=';
    const value = number !== undefined ? String(number) : '';
    const isTagDigit =
      (field?.type ?? param.type) === 'tag-digit-compare' || Array.isArray(tags);

    return isTagDigit
      ? { techname, type: 'tag-digit-compare', selected: [...(tags ?? [])], operator, value }
      : { techname, type: 'digit-compare', operator, value };
  });

const toParam = (item: TRuleParam, rawValue: TRoutingConfigRuleParamValue): IRoutingConfigRuleParam => ({
  name: item.techname,
  type: item.type,
  rawValue,
  // Only rawValue reaches the PUT and the diff; the server derives its own parsedValue.
  parsedValue: rawValue,
});

// Form params → GET array shape. Comparators without a number are dropped, as the
// legacy rule form does.
export const toRuleParams = (items: TRuleParam[]): IRoutingConfigRuleParam[] =>
  items.flatMap(item => {
    if (item.type === 'multi-select') return [toParam(item, [...item.selected])];

    const number = item.value.trim() === '' ? NaN : Number(item.value);
    if (Number.isNaN(number)) return [];

    const comparator = { [OPERATOR_TO_KEY[item.operator]]: number };

    return [
      toParam(
        item,
        item.type === 'tag-digit-compare' ? { ...comparator, tags: [...item.selected] } : comparator,
      ),
    ];
  });
